import type { VendorActivityItem } from '../../../common/models/feature-usage.model';

/** Pages opened by one person (or credits charged for one thing) this close together read as one line. */
export const PAGE_RUN_GAP_MS = 30 * 60 * 1000;

/** A line of the timeline: one item, or a run of pages opened. */
export type TimelineLine =
  | { key: string; kind: 'change' | 'signin'; at: string; item: VendorActivityItem }
  /** Charges for the same thing close together, summed: `total` credits over `charges` charges. */
  | { key: string; kind: 'credits'; at: string; since: string; item: VendorActivityItem; total: number; charges: number }
  /** `at` is the newest page's time, `since` the oldest's; `pages` in the order they were opened. */
  | { key: string; kind: 'pages'; at: string; since: string; item: VendorActivityItem; pages: string[] };

/**
 * The items (newest first) as lines: a person's consecutive page views close
 * together become one line, its pages in the order they were opened, a page
 * opened twice in a row named once.
 */
export function toLines(items: VendorActivityItem[]): TimelineLine[] {
  const lines: TimelineLine[] = [];
  items.forEach((item, i) => {
    const key = `${item.kind}-${item.at}-${i}`;
    const last = lines[lines.length - 1];
    const close = (since: string) => new Date(since).getTime() - new Date(item.at).getTime() <= PAGE_RUN_GAP_MS;
    if (item.kind === 'credits') {
      const credits = item.credits || 0;
      if (last?.kind === 'credits' && last.item.reason === item.reason && Math.sign(last.total) === Math.sign(credits) && close(last.since)) {
        last.total += credits;
        last.charges += 1;
        last.since = item.at;
        return;
      }
      lines.push({ key, kind: 'credits', at: item.at, since: item.at, item, total: credits, charges: 1 });
      return;
    }
    if (item.kind !== 'page') {
      lines.push({ key, kind: item.kind, at: item.at, item });
      return;
    }
    const page = item.page || '/';
    if (last?.kind === 'pages' && last.item.user === item.user && close(last.since)) {
      // Newest come first, so an older page goes in front.
      if (last.pages[0] !== page) last.pages.unshift(page);
      last.since = item.at;
      return;
    }
    lines.push({ key, kind: 'pages', at: item.at, since: item.at, item, pages: [page] });
  });
  return lines;
}

/** Lines by day, newest day first: [day (YYYY-MM-DD, local), lines]. */
export function byDay(lines: TimelineLine[]): [string, TimelineLine[]][] {
  const days = new Map<string, TimelineLine[]>();
  for (const line of lines) {
    const d = new Date(line.at);
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    days.set(day, [...(days.get(day) || []), line]);
  }
  return [...days.entries()];
}
